/**
 * Hook de la pestaña "Conversación" del asistente (/assistant): lista de conversaciones,
 * historial y turnos en vivo por streaming (SSE).
 *
 * El turno corre en el servidor aunque la pantalla se cierre: salir de la pestaña solo corta la
 * conexión. Al volver (o al abrir una conversación con `turn_active`) la pantalla se engancha a
 * `GET /:id/stream` (repaso de lo ya dicho + lo que sigue) y al terminar recarga el historial.
 * "Detener" pide `POST /:id/stop`.
 * Acciones propuestas por el asistente (tramo 6): `GET /:id/actions` al abrir la conversación, al
 * terminar cada turno y tras confirmar/cancelar con `POST /:id/actions/:pid/{confirm,cancel}`.
 * Con el servidor viejo (sin esas rutas) la lista queda vacía y no se muestra nada. Con el servidor viejo (sin `turn_active`, sin `/stop`) se
 * comporta como antes: detener = cortar la conexión.
 * Avisos del asistente ("habla primero", 2026-10-12): `alerts` de `GET /:id` (estado vivo de cada
 * aviso), botones `POST /:id/alerts/:aid/{follow,dismiss}`, evento en vivo `assistant_chat` del
 * canal /api/events y un sondeo de 30 s por si el evento no llega. Con el servidor viejo (sin
 * `alerts`) la tarjeta usa lo guardado en el mensaje.
 *
 * Endpoints: /api/assistant/sessions/* (cookie auth, mismo origen).
 * El parser SSE está calcado de hooks/useClaimAssistant.ts (parseSseStream).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../api/client';
import { useSSEStore } from '../stores/sseStore';
import { useAuthStore } from '../stores/authStore';
import type {
  AlertCard,
  ClaimOptions,
  MessageBlock,
  MessageRow,
  PendingAlert,
  PendingCard,
  SessionRow,
} from '../pages/assistant/shared';
import {
  RESOLVE_FAIL,
  resolveErrorText,
  resolveOutcomeText,
} from '../pages/assistant/claimResolve';

const BASE = '/api/assistant/sessions';
const SEND_FAIL = 'No se pudo enviar el mensaje.';

export interface TurnUsage {
  tokens_in: number;
  tokens_out: number;
}

export type StreamChunk =
  | { kind: 'text'; text: string }
  | { kind: 'tool'; name: string; input_summary: string }
  | { kind: 'done'; usage: TurnUsage; duration_ms: number }
  /** before_stream=true: el servidor rechazó el mensaje antes de empezar a responder (no lo guardó). */
  | { kind: 'error'; message: string; before_stream?: boolean }
  /** No hay turno en curso para engancharse. */
  | { kind: 'idle' }
  /** El servidor aceptó el mensaje y empezó a responder (el turno ya corre allá). */
  | { kind: 'open' };

/** Parser SSE simple: yield-ea {event, data} mientras llegan fragmentos. */
async function* parseSseStream(body: ReadableStream<Uint8Array>) {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buf = '';
  let pendingCr = false;
  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      // Aceptar cortes de línea \r\n (o \r solo): se normalizan a \n. Un \r al final del
      // fragmento se guarda hasta ver si el siguiente empieza con \n.
      let chunk: string = (pendingCr ? '\r' : '') + decoder.decode(value, { stream: true });
      pendingCr = chunk.endsWith('\r');
      if (pendingCr) chunk = chunk.slice(0, -1);
      buf += chunk.replace(/\r\n?/g, '\n');
      let sep: number;
      while ((sep = buf.indexOf('\n\n')) !== -1) {
        const block = buf.slice(0, sep);
        buf = buf.slice(sep + 2);
        let event = 'message';
        const dataLines: string[] = [];
        for (const line of block.split('\n')) {
          if (line.startsWith('event:')) event = line.slice(6).trim();
          else if (line.startsWith('data:')) dataLines.push(line.slice(5).trim());
        }
        // Líneas de "mantener viva" la conexión (": keepalive …"): sin datos, se ignoran.
        if (dataLines.length === 0) continue;
        const raw = dataLines.join('\n');
        let data: unknown = raw;
        try {
          data = JSON.parse(raw);
        } catch {
          /* keep as string */
        }
        yield { event, data };
      }
    }
  } finally {
    reader.releaseLock();
  }
}

async function fetchSse(url: string, opts: RequestInit): Promise<Response> {
  let res = await fetch(url, opts);
  if (res.status === 401) {
    // Mismo criterio que el cliente de la API: renovar el acceso una vez y reintentar.
    const refresh = await fetch('/api/auth/refresh', { method: 'POST', credentials: 'include' });
    if (!refresh.ok) {
      window.location.href = '/login';
      return res;
    }
    res = await fetch(url, opts);
  }
  return res;
}

function postMessage(sessionId: number, message: string, signal: AbortSignal): Promise<Response> {
  return fetchSse(`${BASE}/${sessionId}/messages`, {
    method: 'POST',
    credentials: 'include',
    cache: 'no-store',
    headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream' },
    body: JSON.stringify({ message }),
    signal,
  });
}

/** Traduce los eventos SSE de un turno a fragmentos; termina en done/error/idle. */
async function* readTurn(body: ReadableStream<Uint8Array>): AsyncGenerator<StreamChunk> {
  for await (const { event, data } of parseSseStream(body)) {
    const d = (data ?? {}) as Record<string, unknown>;
    if (event === 'text') {
      if (typeof d.text === 'string' && d.text) yield { kind: 'text', text: d.text };
    } else if (event === 'tool') {
      yield {
        kind: 'tool',
        name: String(d.name ?? ''),
        input_summary: String(d.input_summary ?? ''),
      };
    } else if (event === 'done') {
      const u = (d.usage ?? {}) as Partial<TurnUsage>;
      yield {
        kind: 'done',
        usage: {
          tokens_in: Number(u.tokens_in) || 0,
          tokens_out: Number(u.tokens_out) || 0,
        },
        duration_ms: Number(d.duration_ms) || 0,
      };
      return;
    } else if (event === 'error') {
      yield {
        kind: 'error',
        message: typeof d.message === 'string' && d.message ? d.message : SEND_FAIL,
      };
      return;
    } else if (event === 'idle') {
      yield { kind: 'idle' };
      return;
    }
  }
}

/**
 * Se engancha al turno en curso (o recién terminado) de la conversación: primero lo ya dicho,
 * después lo que siga. Sin turno (o servidor viejo sin esta ruta) → un solo `idle`.
 */
export async function* attachTurn(
  sessionId: number,
  signal: AbortSignal
): AsyncGenerator<StreamChunk> {
  const res = await fetchSse(`${BASE}/${sessionId}/stream`, {
    method: 'GET',
    credentials: 'include',
    cache: 'no-store',
    headers: { Accept: 'text/event-stream' },
    signal,
  });
  const isSse = (res.headers.get('content-type') ?? '').includes('text/event-stream');
  if (res.status !== 200 || !isSse || !res.body) {
    yield { kind: 'idle' };
    return;
  }
  yield* readTurn(res.body);
}

/** Corre un turno del asistente y va entregando los fragmentos que llegan. */
export async function* streamTurn(
  sessionId: number,
  message: string,
  signal: AbortSignal
): AsyncGenerator<StreamChunk> {
  const res = await postMessage(sessionId, message, signal);
  const isSse = (res.headers.get('content-type') ?? '').includes('text/event-stream');
  if (res.status !== 200 || !isSse || !res.body) {
    let msg = SEND_FAIL;
    try {
      const j = (await res.json()) as { error?: string };
      if (j?.error) msg = j.error;
    } catch {
      /* respuesta sin cuerpo legible */
    }
    yield { kind: 'error', message: msg, before_stream: true };
    return;
  }
  yield { kind: 'open' };
  yield* readTurn(res.body);
}

const LAST_SESSION_KEY = 'cbt_assistant_last_session';
/** Reintentos para volver a engancharse si la conexión se corta a mitad de una respuesta. */
const REATTACH_TRIES = 5;
const CUT_MESSAGE = 'Se cortó la conexión; recargá la conversación para ver la respuesta.';
/** Espera máxima tras "Detener" a que el servidor confirme el corte antes de soltar la conexión. */
const STOP_GRACE_MS = 10_000;

function rememberSession(id: number | null) {
  try {
    if (id === null) sessionStorage.removeItem(LAST_SESSION_KEY);
    else sessionStorage.setItem(LAST_SESSION_KEY, String(id));
  } catch {
    /* navegador sin almacenamiento: no pasa nada */
  }
}

function rememberedSession(): number | null {
  try {
    const n = Number(sessionStorage.getItem(LAST_SESSION_KEY));
    return Number.isSafeInteger(n) && n > 0 ? n : null;
  } catch {
    return null;
  }
}

/** true si el mensaje es un aviso del asistente (`system` con `{ alert }`). */
function isAlertMessage(m: MessageRow): boolean {
  if (m.role !== 'system') return false;
  const a = (m.content as { alert?: unknown }).alert;
  return !!a && typeof a === 'object';
}

/**
 * Historial hasta el último mensaje del usuario o el último aviso (lo posterior llega en vivo al
 * engancharse). El aviso cuenta como corte porque el turno que lo revisa no guarda mensaje del
 * usuario: sin esto, la tarjeta desaparecía mientras el asistente la revisaba.
 */
function historyBeforeLiveTurn(messages: MessageRow[]): MessageRow[] {
  let cut = -1;
  messages.forEach((m, i) => {
    if (m.role === 'user' || isAlertMessage(m)) cut = i;
  });
  return cut >= 0 ? messages.slice(0, cut + 1) : messages;
}

/** Id del último mensaje guardado (los locales tienen id negativo). */
function lastSavedId(messages: MessageRow[]): string | null {
  for (let i = messages.length - 1; i >= 0; i--) {
    const id = Number(messages[i].id);
    if (Number.isFinite(id) && id > 0) return String(messages[i].id);
  }
  return null;
}

/** Avisos de la conversación indexados por id. */
function alertsById(list: AlertCard[] | undefined): Record<number, AlertCard> {
  const out: Record<number, AlertCard> = {};
  for (const a of list ?? []) if (a && typeof a.id === 'number') out[a.id] = a;
  return out;
}

/** Sondeo de respaldo de la conversación abierta (por si el evento en vivo no llega). */
const POLL_MS = 30_000;
const ALERT_FAIL = 'No se pudo marcar el aviso.';
const FOLLOW_FAIL = 'No se pudo seguir el aviso.';
const PENDING_FAIL = 'No se pudieron cargar los avisos pendientes.';

let localSeq = -1;
function localMessage(
  sessionId: number,
  role: MessageRow['role'],
  content: MessageRow['content']
): MessageRow {
  return {
    id: localSeq--,
    session_id: sessionId,
    role,
    content,
    created_at: new Date().toISOString(),
  };
}

/** Pedido de resolver un reclamo desde la tarjeta. */
export interface ResolveClaimBody {
  kind: string;
  percentage?: number;
  reason?: string;
}

/** Resultado de resolver un reclamo desde la tarjeta (línea ✓/✗ debajo de la fila). */
export interface ResolveClaimResult {
  executed: boolean;
  text: string;
}

/** Resultado de apretar "Confirmar" en una tarjeta (se muestra arriba de las tarjetas). */
export interface ActionOutcome {
  id: number;
  label: string;
  ok: boolean;
  text: string;
}

export function useAssistantChat() {
  const [sessions, setSessions] = useState<SessionRow[]>([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [current, setCurrent] = useState<SessionRow | null>(null);
  const [messages, setMessages] = useState<MessageRow[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [streaming, setStreaming] = useState(false);
  const [liveText, setLiveText] = useState('');
  const [liveTools, setLiveTools] = useState<string[]>([]);
  const [lastUsage, setLastUsage] = useState<{ usage: TurnUsage; duration_ms: number } | null>(
    null
  );
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  /** true mientras ESTA pantalla está enviando un mensaje (no cuando solo mira un turno). */
  const [sending, setSending] = useState(false);
  /** true desde que se pide crear/enviar hasta que termina (evita doble envío). */
  const busyRef = useRef(false);
  /** Conexión del envío en curso. */
  const abortRef = useRef<AbortController | null>(null);
  /** Conexión de "mirar" un turno que ya corría en el servidor. */
  const attachRef = useRef<AbortController | null>(null);
  /** Conversación cuyo turno se está mostrando en vivo (para "Detener"). */
  const liveSidRef = useRef<number | null>(null);
  /** El usuario apretó "Detener": no mostrar el aviso de cancelado como error. */
  const stoppedRef = useRef(false);
  const mountedRef = useRef(true);
  const openSeq = useRef(0);
  /** Acciones propuestas de la conversación abierta: pendientes y últimas cerradas. */
  const [pendingActions, setPendingActions] = useState<PendingCard[]>([]);
  const [recentActions, setRecentActions] = useState<PendingCard[]>([]);
  /** Acción que se está confirmando o cancelando ahora (evita doble clic). */
  const [actionBusy, setActionBusy] = useState<number | null>(null);
  /**
   * Tarjeta con una resolución de reclamo en curso. Aparte de `actionBusy` para que el botón
   * "Confirmar" de esa misma tarjeta no muestre que se está confirmando; las tarjetas se
   * bloquean igual mientras dura (ver `ActionsPanel`).
   */
  const [resolvingBusy, setResolvingBusy] = useState<number | null>(null);
  const [actionOutcome, setActionOutcome] = useState<ActionOutcome | null>(null);
  /** Conversación a la que pertenecen las acciones mostradas (descarta respuestas viejas). */
  const actionsSidRef = useRef<number | null>(null);
  /** Tarjetas con una resolución de reclamo en vuelo (evita el doble pedido). */
  const resolvingRef = useRef<Set<number>>(new Set());
  /** Estado vivo de los avisos de la conversación abierta (pisa al guardado en el mensaje). */
  const [alerts, setAlerts] = useState<Record<number, AlertCard>>({});
  /** Aviso cuyo botón se está procesando (evita doble clic). */
  const [alertBusy, setAlertBusy] = useState<number | null>(null);
  /** Tope de conversaciones pedidas al servidor; "Mostrar más" lo sube de a 100 (2026-10-21). */
  const sessionsLimitRef = useRef(30);
  const [sessionsLimit, setSessionsLimit] = useState(30);
  /** Avisos pendientes de TODAS las conversaciones (vista única, 2026-10-21). */
  const [pendingAlerts, setPendingAlerts] = useState<{
    items: PendingAlert[];
    total: number;
  } | null>(null);
  const [pendingAlertsLoading, setPendingAlertsLoading] = useState(false);
  /** Copias para leer dentro del evento en vivo y del sondeo (sin rehacer la suscripción). */
  const currentRef = useRef<SessionRow | null>(null);
  const messagesRef = useRef<MessageRow[]>([]);
  const sessionsRef = useRef<SessionRow[]>([]);
  useEffect(() => {
    currentRef.current = current;
  }, [current]);
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);
  useEffect(() => {
    sessionsRef.current = sessions;
  }, [sessions]);
  /** true si esta pantalla ya está mostrando un turno en vivo (propio o mirado). */
  const isLive = useCallback(() => !!(attachRef.current || abortRef.current), []);

  const clearActions = useCallback((sid: number | null) => {
    actionsSidRef.current = sid;
    setPendingActions([]);
    setRecentActions([]);
    setActionOutcome(null);
    setAlerts({});
  }, []);

  /** Recarga las acciones de la conversación (silencioso: si falla, no cambia nada). */
  const loadActions = useCallback(async (sid?: number) => {
    const id = sid ?? actionsSidRef.current;
    if (id == null) return;
    try {
      const res = await api.get<{ pending: PendingCard[]; recent: PendingCard[] }>(
        `/assistant/sessions/${id}/actions`
      );
      if (!mountedRef.current || actionsSidRef.current !== id) return;
      if (res.success && res.data) {
        setPendingActions(res.data.pending ?? []);
        setRecentActions(res.data.recent ?? []);
      }
    } catch {
      /* servidor viejo o corte: sin tarjetas */
    }
  }, []);

  /**
   * Recarga la lista. `quiet` (evento en vivo / sondeo): sin ruedita ni aviso de error.
   * Devuelve la lista nueva, o null si no se pudo.
   */
  const loadSessions = useCallback(async (quiet = false): Promise<SessionRow[] | null> => {
    if (!quiet) setSessionsLoading(true);
    try {
      const res = await api.get<SessionRow[]>(
        `/assistant/sessions?limit=${sessionsLimitRef.current}`
      );
      if (res.success && res.data) {
        const list = res.data;
        setSessions(list);
        // Mantener al día los datos de la conversación abierta (título, estado).
        setCurrent((c) => (c ? (list.find((s) => s.id === c.id) ?? c) : c));
        return list;
      }
      if (!quiet) setError(res.error || 'No se pudieron cargar las conversaciones.');
    } catch {
      if (!quiet) setError('No se pudieron cargar las conversaciones.');
    } finally {
      if (!quiet) setSessionsLoading(false);
    }
    return null;
  }, []);

  /** Deja de mirar el turno en vivo (solo corta la conexión: el turno sigue en el servidor). */
  const detach = useCallback(() => {
    attachRef.current?.abort();
    attachRef.current = null;
    liveSidRef.current = null;
    setStreaming(false);
    setLiveText('');
    setLiveTools([]);
  }, []);

  /**
   * Recarga el historial guardado de la conversación (tras terminar un turno mirado en vivo).
   * `quiet` (evento en vivo / sondeo): la lista se refresca sin ruedita ni aviso de error.
   * Si mientras tanto arrancó un envío o un turno en vivo, la respuesta se descarta (pisaría lo
   * que se está mostrando).
   */
  const reloadHistory = useCallback(
    async (id: number, seq: number, quiet = false) => {
      try {
        const res = await api.get<{
          session: SessionRow;
          messages: MessageRow[];
          alerts?: AlertCard[];
        }>(`/assistant/sessions/${id}`);
        if (seq !== openSeq.current || !mountedRef.current) return;
        if (busyRef.current || isLive()) return;
        if (res.success && res.data) {
          setCurrent(res.data.session);
          setMessages(res.data.messages ?? []);
          setAlerts(alertsById(res.data.alerts));
        }
      } catch {
        /* se verá al volver a abrirla */
      }
      if (mountedRef.current) {
        void loadSessions(quiet);
        void loadActions(id);
      }
    },
    [loadSessions, loadActions, isLive]
  );

  /** Se engancha al turno que corre en el servidor y lo muestra en vivo hasta que termina. */
  const attach = useCallback(
    async (sid: number, seq: number) => {
      attachRef.current?.abort();
      const ctl = new AbortController();
      attachRef.current = ctl;
      liveSidRef.current = sid;
      stoppedRef.current = false;
      setLiveText('');
      setLiveTools([]);
      setStreaming(true);
      let turnError: string | null = null;
      let usage: { usage: TurnUsage; duration_ms: number } | null = null;
      try {
        // Si la conexión se corta sin aviso de fin (red, suspensión), el turno sigue en el
        // servidor: se vuelve a enganchar con esperas de 1, 2, 4, 8 y 16 s. Cada enganche repasa
        // el turno desde el principio, así que lo mostrado se reemplaza al llegar el primer dato.
        for (let attempt = 0; ; attempt++) {
          let finished = false;
          let fresh = attempt > 0;
          try {
            for await (const c of attachTurn(sid, ctl.signal)) {
              if (seq !== openSeq.current) break;
              if (fresh && (c.kind === 'text' || c.kind === 'tool')) {
                fresh = false;
                setLiveText('');
                setLiveTools([]);
              }
              if (c.kind === 'text') setLiveText((t) => t + c.text);
              else if (c.kind === 'tool') setLiveTools((t) => [...t, c.name]);
              else if (c.kind === 'done') usage = { usage: c.usage, duration_ms: c.duration_ms };
              else if (c.kind === 'error') turnError = c.message;
              if (c.kind === 'done' || c.kind === 'error' || c.kind === 'idle') finished = true;
            }
          } catch {
            /* corte de red: se reintenta abajo */
          }
          if (finished || ctl.signal.aborted || seq !== openSeq.current || !mountedRef.current)
            break;
          if (attempt >= REATTACH_TRIES) {
            turnError = CUT_MESSAGE;
            break;
          }
          await new Promise<void>((ok) => {
            const t = setTimeout(ok, 1000 * 2 ** attempt);
            ctl.signal.addEventListener('abort', () => (clearTimeout(t), ok()), { once: true });
          });
          if (ctl.signal.aborted) break;
        }
      } finally {
        if (attachRef.current === ctl) attachRef.current = null;
        if (seq === openSeq.current && mountedRef.current) {
          liveSidRef.current = null;
          setLiveText('');
          setLiveTools([]);
          setStreaming(false);
          if (usage) setLastUsage(usage);
          if (turnError && !stoppedRef.current) setError(turnError);
          // Lo dicho en el turno ya quedó guardado en el servidor: se trae el historial completo.
          void reloadHistory(sid, seq);
        }
      }
    },
    [reloadHistory]
  );

  const openSessionInner = useCallback(
    async (id: number, quiet: boolean) => {
      if (busyRef.current) return; // no cambiar de conversación a mitad de un envío
      const seq = ++openSeq.current;
      detach();
      clearActions(id);
      setHistoryLoading(true);
      setError(null);
      setLastUsage(null);
      try {
        const res = await api.get<{
          session: SessionRow;
          messages: MessageRow[];
          pending_actions?: PendingCard[];
          alerts?: AlertCard[];
        }>(`/assistant/sessions/${id}`);
        if (seq !== openSeq.current) return;
        if (res.success && res.data) {
          const s = res.data.session;
          const all = res.data.messages ?? [];
          rememberSession(s.id);
          setCurrent(s);
          setAlerts(alertsById(res.data.alerts));
          // Abrirla marcó vistos los avisos nuevos: el contador de la lista baja.
          if ((res.data.alerts ?? []).length > 0) void loadSessions(true);
          setPendingActions(res.data.pending_actions ?? []);
          void loadActions(s.id);
          // Turno corriendo en el servidor: lo de este turno se ve en vivo (no duplicado).
          setMessages(s.turn_active ? historyBeforeLiveTurn(all) : all);
          if (s.turn_active) void attach(s.id, seq);
        } else if (quiet) {
          rememberSession(null);
        } else {
          setError(res.error || 'No se pudo abrir la conversación.');
        }
      } catch {
        if (seq === openSeq.current && !quiet) setError('No se pudo abrir la conversación.');
      } finally {
        if (seq === openSeq.current) setHistoryLoading(false);
      }
    },
    [attach, detach, clearActions, loadActions, loadSessions]
  );

  const openSession = useCallback((id: number) => openSessionInner(id, false), [openSessionInner]);

  const createSessionRaw = useCallback(async (): Promise<SessionRow | null> => {
    setError(null);
    setCreating(true);
    try {
      const res = await api.post<SessionRow>('/assistant/sessions', {});
      if (res.success && res.data) {
        const s = res.data;
        ++openSeq.current;
        detach();
        clearActions(s.id);
        rememberSession(s.id);
        setCurrent(s);
        setMessages([]);
        setLastUsage(null);
        setSessions((list) => [s, ...list.filter((x) => x.id !== s.id)]);
        return s;
      }
      setError(res.error || 'No se pudo crear la conversación.');
    } catch {
      setError('No se pudo crear la conversación.');
    } finally {
      setCreating(false);
    }
    return null;
  }, [detach, clearActions]);

  /** "Nueva conversación" desde la pantalla: ignorada si ya se está creando o respondiendo. */
  const createSession = useCallback(async (): Promise<SessionRow | null> => {
    if (busyRef.current) return null;
    busyRef.current = true;
    try {
      return await createSessionRaw();
    } finally {
      busyRef.current = false;
    }
  }, [createSessionRaw]);

  const closeSession = useCallback(
    async (id: number) => {
      setError(null);
      try {
        const res = await api.delete<unknown>(`/assistant/sessions/${id}`);
        if (res.success) {
          setCurrent((c) =>
            c && c.id === id ? { ...c, status: 'closed', closed_at: new Date().toISOString() } : c
          );
          void loadSessions();
        } else {
          setError(res.error || 'No se pudo cerrar la conversación.');
        }
      } catch {
        setError('No se pudo cerrar la conversación.');
      }
    },
    [loadSessions]
  );

  /**
   * Elimina la conversación para siempre (con sus mensajes). Si era la abierta, la vista queda
   * vacía. Devuelve true si se eliminó. Con el servidor viejo (sin esta función) se muestra su
   * aviso y no se toca nada.
   */
  const deleteSession = useCallback(
    async (id: number): Promise<boolean> => {
      setError(null);
      try {
        const res = await api.delete<unknown>(`/assistant/sessions/${id}/permanent`);
        if (!res.success) {
          setError(res.error || 'No se pudo eliminar la conversación.');
          return false;
        }
      } catch {
        setError('No se pudo eliminar la conversación.');
        return false;
      }
      setSessions((list) => list.filter((s) => s.id !== id));
      if (rememberedSession() === id) rememberSession(null);
      if (current?.id === id) {
        ++openSeq.current;
        abortRef.current?.abort();
        detach();
        clearActions(null);
        setCurrent(null);
        setMessages([]);
        setLastUsage(null);
      }
      return true;
    },
    [current, detach, clearActions]
  );

  const sendInner = useCallback(
    async (message: string): Promise<string | null> => {
      let session = current;
      if (!session) {
        session = await createSessionRaw();
        if (!session) return message;
      }
      if (session.status === 'closed') {
        setError('Esa conversación está cerrada.');
        return message;
      }
      const sid = session.id;
      const ctl = new AbortController();
      abortRef.current = ctl;
      liveSidRef.current = sid;
      stoppedRef.current = false;
      setError(null);
      setLastUsage(null);
      setLiveText('');
      setLiveTools([]);
      setStreaming(true);
      const userMsg = localMessage(sid, 'user', { text: message });
      setMessages((m) => [...m, userMsg]);

      // Bloques en el orden en que llegan, para guardarlos en el historial al terminar.
      const blocks: MessageBlock[] = [];
      let turnError: string | null = null;
      let ended = false;
      let rejected = false;
      /** El servidor aceptó el mensaje: el turno corre allá aunque se corte la conexión. */
      let opened = false;
      try {
        for await (const c of streamTurn(sid, message, ctl.signal)) {
          if (c.kind === 'open') opened = true;
          else if (c.kind === 'text') {
            const last = blocks[blocks.length - 1];
            if (last && last.type === 'text') last.text += c.text;
            else blocks.push({ type: 'text', text: c.text });
            setLiveText((t) => t + c.text);
          } else if (c.kind === 'tool') {
            blocks.push({ type: 'tool_use', name: c.name, input_summary: c.input_summary });
            setLiveTools((t) => [...t, c.name]);
          } else if (c.kind === 'done') {
            ended = true;
            setLastUsage({ usage: c.usage, duration_ms: c.duration_ms });
          } else if (c.kind === 'error') {
            ended = true;
            turnError = c.message;
            rejected = !!c.before_stream;
          }
        }
        // El canal se cerró sin aviso de fin (corte de red): avisarlo en vez de quedar mudo.
        if (!ended && !ctl.signal.aborted) turnError = 'Se cortó la conexión con el asistente.';
      } catch (err) {
        if ((err as Error)?.name !== 'AbortError')
          turnError = 'Se cortó la conexión con el asistente.';
      } finally {
        abortRef.current = null;
        liveSidRef.current = null;
        // Corte a mitad de la respuesta: no es un error, el turno sigue en el servidor. Se
        // vuelve a enganchar (repaso + vivo) y al terminar se recarga el historial.
        if (opened && !ended && !ctl.signal.aborted && mountedRef.current) {
          void attach(sid, openSeq.current);
        } else if (rejected) {
          // El servidor no guardó el mensaje: sacarlo del historial local (vuelve al cuadro de texto).
          setMessages((m) => m.filter((x) => x.id !== userMsg.id));
        } else {
          const add: MessageRow[] = [];
          if (blocks.length > 0) add.push(localMessage(sid, 'assistant', { blocks }));
          if (turnError) add.push(localMessage(sid, 'system', { error: turnError }));
          if (add.length) setMessages((m) => [...m, ...add]);
        }
        if (!attachRef.current) {
          if (turnError && !stoppedRef.current) setError(turnError);
          setLiveText('');
          setLiveTools([]);
          setStreaming(false);
          void loadSessions();
          // El turno pudo haber propuesto (o ejecutado por "confirmo") acciones.
          if (!rejected) void loadActions(sid);
        }
      }
      return rejected ? message : null;
    },
    [current, createSessionRaw, loadSessions, attach, loadActions]
  );

  /** Botón "Confirmar" de una tarjeta: ejecuta la acción en el servidor y muestra el resultado. */
  const confirmAction = useCallback(
    async (pid: number) => {
      const sid = actionsSidRef.current;
      if (sid == null || actionBusy !== null || resolvingBusy !== null) return;
      const card = pendingActions.find((a) => a.id === pid);
      const label = card?.label ?? 'Acción';
      setActionBusy(pid);
      setActionOutcome(null);
      try {
        const res = await api.post<{
          executed: boolean;
          detail: string;
          error?: string;
          turn_started?: boolean;
        }>(`/assistant/sessions/${sid}/actions/${pid}/confirm`, {});
        if (actionsSidRef.current !== sid) return;
        if (res.success && res.data) {
          const d = res.data;
          setActionOutcome(
            d.executed
              ? { id: pid, label, ok: true, text: d.detail }
              : { id: pid, label, ok: false, text: d.error || d.detail || 'No se ejecutó.' }
          );
          // El servidor arrancó la continuación del agente: se muestra en vivo, como si le
          // hubiéramos escrito.
          if (d.turn_started && sid === current?.id && !busyRef.current)
            void attach(sid, openSeq.current);
        } else {
          setActionOutcome({
            id: pid,
            label,
            ok: false,
            text: res.error || 'No se pudo confirmar la acción.',
          });
        }
      } catch {
        if (actionsSidRef.current === sid)
          setActionOutcome({ id: pid, label, ok: false, text: 'No se pudo confirmar la acción.' });
      } finally {
        if (mountedRef.current) setActionBusy(null);
        void loadActions(sid);
      }
    },
    [actionBusy, resolvingBusy, pendingActions, loadActions, attach, current]
  );

  /**
   * Fila "Resolver el reclamo" de una tarjeta: qué permite Mercado Libre ahora.
   * null = no se pudo consultar (la tarjeta muestra el aviso y "Actualizar").
   */
  const loadClaimOptions = useCallback(async (pid: number): Promise<ClaimOptions | null> => {
    const sid = actionsSidRef.current;
    if (sid == null) return null;
    try {
      const res = await api.get<ClaimOptions>(
        `/assistant/sessions/${sid}/actions/${pid}/claim-options`
      );
      if (actionsSidRef.current !== sid) return null;
      return res.success && res.data ? res.data : null;
    } catch {
      return null;
    }
  }, []);

  /**
   * Botón de resolución de la fila "Resolver el reclamo" (ya confirmado en la tarjeta). Bloquea
   * las tarjetas mientras corre; si el servidor arrancó la continuación del agente, se muestra en
   * vivo como tras "Confirmar". Devuelve null si se descartó (doble pedido u otra conversación).
   */
  const resolveClaim = useCallback(
    async (pid: number, body: ResolveClaimBody): Promise<ResolveClaimResult | null> => {
      const sid = actionsSidRef.current;
      if (
        sid == null ||
        actionBusy !== null ||
        resolvingBusy !== null ||
        resolvingRef.current.has(pid)
      )
        return null;
      resolvingRef.current.add(pid);
      setResolvingBusy(pid);
      setActionOutcome(null);
      try {
        const res = await api.post<{
          executed: boolean;
          detail: string;
          action_id?: number;
          turn_started?: boolean;
          error?: string;
        }>(`/assistant/sessions/${sid}/actions/${pid}/resolve`, body);
        if (actionsSidRef.current !== sid) return null;
        if (res.success && res.data) {
          const d = res.data;
          if (d.turn_started && sid === current?.id && !busyRef.current)
            void attach(sid, openSeq.current);
          return {
            executed: !!d.executed,
            text: resolveOutcomeText({
              executed: !!d.executed,
              detail: d.detail || (d.executed ? 'Hecho.' : RESOLVE_FAIL),
            }),
          };
        }
        // Error del servidor: su texto ya es español simple; texto fijo solo si llega vacío.
        return { executed: false, text: `✗ ${resolveErrorText(res.error)}` };
      } catch {
        return actionsSidRef.current === sid
          ? { executed: false, text: `✗ ${RESOLVE_FAIL}` }
          : null;
      } finally {
        resolvingRef.current.delete(pid);
        if (mountedRef.current) setResolvingBusy(null);
        void loadActions(sid);
      }
    },
    [actionBusy, resolvingBusy, loadActions, attach, current]
  );

  /** Botón "Cancelar" de una tarjeta. */
  const cancelAction = useCallback(
    async (pid: number) => {
      const sid = actionsSidRef.current;
      if (sid == null || actionBusy !== null || resolvingBusy !== null) return;
      const label = pendingActions.find((a) => a.id === pid)?.label ?? 'Acción';
      setActionBusy(pid);
      setActionOutcome(null);
      try {
        const res = await api.post<{ turn_started?: boolean }>(
          `/assistant/sessions/${sid}/actions/${pid}/cancel`,
          {}
        );
        if (actionsSidRef.current === sid && !res.success)
          setActionOutcome({
            id: pid,
            label,
            ok: false,
            text: res.error || 'No se pudo cancelar la acción.',
          });
        if (
          actionsSidRef.current === sid &&
          res.success &&
          res.data?.turn_started &&
          sid === current?.id &&
          !busyRef.current
        )
          void attach(sid, openSeq.current);
      } catch {
        if (actionsSidRef.current === sid)
          setActionOutcome({ id: pid, label, ok: false, text: 'No se pudo cancelar la acción.' });
      } finally {
        if (mountedRef.current) setActionBusy(null);
        void loadActions(sid);
      }
    },
    [actionBusy, resolvingBusy, pendingActions, loadActions, attach, current]
  );

  /**
   * Botón "Ver y seguir" de un aviso: lo marca seguido y el asistente arranca a revisarlo (se ve
   * en vivo). Si la conversación ya está respondiendo, el servidor contesta 409 con su texto.
   */
  /** "Mostrar más" de la lista de conversaciones (2026-10-21): pide 100 más (tope 500) y recarga. */
  const loadMoreSessions = useCallback(async () => {
    sessionsLimitRef.current = Math.min(500, sessionsLimitRef.current + 100);
    setSessionsLimit(sessionsLimitRef.current);
    await loadSessions();
  }, [loadSessions]);

  /**
   * Avisos pendientes de TODAS las conversaciones (vista única, 2026-10-21). Filtros opcionales
   * por empresa y tipo; los más nuevos primero. Si falla, avisa y deja lo que había.
   */
  const loadPendingAlerts = useCallback(
    async (f: { seller_id?: number | null; kind?: string | null } = {}) => {
      setPendingAlertsLoading(true);
      try {
        const q = new URLSearchParams({ limit: '200' });
        if (f.seller_id) q.set('seller_id', String(f.seller_id));
        if (f.kind) q.set('kind', f.kind);
        const res = await api.get<{ items: PendingAlert[]; total: number }>(
          `/assistant/sessions/alerts/pending?${q.toString()}`
        );
        if (!mountedRef.current) return;
        if (res.success && res.data) setPendingAlerts(res.data);
        else setError(res.error || PENDING_FAIL);
      } catch {
        if (mountedRef.current) setError(PENDING_FAIL);
      } finally {
        if (mountedRef.current) setPendingAlertsLoading(false);
      }
    },
    []
  );

  /** Reemplaza un aviso en la vista única (si está) con su estado nuevo. */
  const patchPending = useCallback((aid: number, card: AlertCard) => {
    setPendingAlerts((p) =>
      p ? { ...p, items: p.items.map((x) => (x.id === aid ? { ...x, ...card } : x)) } : p
    );
  }, []);

  /**
   * Botón "Ver y seguir" de un aviso de la conversación `sid` (la abierta o cualquier otra, desde
   * la vista única). Si el turno arrancó en la conversación abierta, se mira en vivo.
   */
  const followAlertIn = useCallback(
    async (sid: number | null, aid: number) => {
      if (sid == null || alertBusy !== null) return;
      const isCurrent = currentRef.current?.id === sid;
      if (isCurrent && busyRef.current) return;
      setAlertBusy(aid);
      setError(null);
      try {
        const res = await api.post<{ started: boolean; alert?: AlertCard; text?: string }>(
          `/assistant/sessions/${sid}/alerts/${aid}/follow`,
          {}
        );
        if (!mountedRef.current) return;
        const stillCurrent = currentRef.current?.id === sid;
        if (res.success && res.data) {
          const d = res.data;
          if (d.alert) {
            if (stillCurrent) setAlerts((a) => ({ ...a, [aid]: d.alert! }));
            patchPending(aid, d.alert);
          }
          if (d.started) {
            if (stillCurrent && !isLive()) void attach(sid, openSeq.current);
          } else if (d.text) {
            setError(d.text);
          }
        } else {
          setError(res.error || FOLLOW_FAIL);
        }
      } catch {
        if (mountedRef.current) setError(FOLLOW_FAIL);
      } finally {
        if (mountedRef.current) setAlertBusy(null);
        void loadSessions(true);
      }
    },
    [alertBusy, attach, isLive, loadSessions, patchPending]
  );

  /** Botón "Ver y seguir" de un aviso de la conversación abierta. */
  const followAlert = useCallback(
    (aid: number) => followAlertIn(currentRef.current?.id ?? null, aid),
    [followAlertIn]
  );

  /** Botón "Visto" de un aviso de la conversación `sid`: lo marca visto sin pedirle nada al asistente. */
  const dismissAlertIn = useCallback(
    async (sid: number | null, aid: number) => {
      if (sid == null || alertBusy !== null) return;
      setAlertBusy(aid);
      setError(null);
      try {
        const res = await api.post<{ alert?: AlertCard }>(
          `/assistant/sessions/${sid}/alerts/${aid}/dismiss`,
          {}
        );
        if (!mountedRef.current) return;
        if (res.success && res.data?.alert) {
          const card = res.data.alert;
          if (currentRef.current?.id === sid) setAlerts((a) => ({ ...a, [aid]: card }));
          patchPending(aid, card);
        } else if (!res.success) {
          setError(res.error || ALERT_FAIL);
        }
      } catch {
        if (mountedRef.current) setError(ALERT_FAIL);
      } finally {
        if (mountedRef.current) setAlertBusy(null);
        void loadSessions(true);
      }
    },
    [alertBusy, loadSessions, patchPending]
  );

  /** Botón "Visto" de un aviso de la conversación abierta. */
  const dismissAlert = useCallback(
    (aid: number) => dismissAlertIn(currentRef.current?.id ?? null, aid),
    [dismissAlertIn]
  );

  // Evento en vivo del servidor: llegó un aviso, o arrancó/terminó un turno sin que nadie
  // escribiera (turno automático). Si es la conversación abierta se recarga o se mira en vivo;
  // siempre se refresca la lista (contadores y "respondiendo…").
  useEffect(() => {
    const off = useSSEStore.getState().subscribe('assistant_chat', (raw) => {
      if (!mountedRef.current) return;
      const sid = Number(raw.session_id);
      const what = String(raw.what ?? '');
      if (!Number.isSafeInteger(sid)) return;
      const myId = useAuthStore.getState().user?.id;
      const mine = Number(raw.user_id) === myId;
      const listed = sessionsRef.current.some((s) => s.id === sid);
      const cur = currentRef.current;
      if (cur && cur.id === sid) {
        if (what === 'turn_started') {
          if (!isLive() && !busyRef.current) void attach(sid, openSeq.current);
          void loadSessions(true);
        } else if (!isLive() && !busyRef.current) {
          // 'alert' | 'turn_done': reloadHistory ya refresca la lista. Con un turno en vivo, al
          // terminar se recarga solo.
          void reloadHistory(sid, openSeq.current, true);
        } else {
          void loadSessions(true);
        }
      } else if (mine || listed) {
        void loadSessions(true);
      }
    });
    return off;
  }, [attach, reloadHistory, loadSessions, isLive]);

  // Sondeo de respaldo cada 30 s mientras la pestaña está abierta: lista al día y, si la
  // conversación abierta cambió en el servidor, engancharse al turno o recargar el historial.
  // (El refresco de 20 s de la lista mientras algo responde, en la pestaña, sigue aparte.)
  useEffect(() => {
    const t = setInterval(() => {
      void (async () => {
        const list = await loadSessions(true);
        const cur = currentRef.current;
        if (!list || !cur || !mountedRef.current || currentRef.current?.id !== cur.id) return;
        if (isLive() || busyRef.current) return;
        const row = list.find((s) => s.id === cur.id);
        if (!row) return;
        if (row.turn_active) {
          void attach(row.id, openSeq.current);
          return;
        }
        if (
          row.last_message_id != null &&
          String(row.last_message_id) !== lastSavedId(messagesRef.current)
        )
          void reloadHistory(row.id, openSeq.current, true);
      })();
    }, POLL_MS);
    return () => clearInterval(t);
  }, [loadSessions, attach, reloadHistory, isLive]);

  /**
   * Envía un mensaje. Devuelve el texto a devolver al cuadro de entrada cuando el servidor lo
   * rechazó antes de responder (para reintentar), o null.
   */
  const send = useCallback(
    async (text: string): Promise<string | null> => {
      const message = text.trim();
      if (!message || busyRef.current) return null;
      busyRef.current = true;
      setSending(true);
      try {
        return await sendInner(message);
      } finally {
        busyRef.current = false;
        setSending(false);
      }
    },
    [sendInner]
  );

  /**
   * "Detener": le pide al servidor que corte el turno y espera su aviso de fin (así el próximo
   * mensaje no choca con "ya está respondiendo"). Si el servidor no lo confirma (o es el viejo,
   * que corta al cerrarse la conexión) se corta la conexión local.
   */
  const stop = useCallback(async () => {
    const sid = liveSidRef.current;
    const ctl = abortRef.current ?? attachRef.current;
    if (sid === null || !ctl) {
      abortRef.current?.abort();
      attachRef.current?.abort();
      return;
    }
    stoppedRef.current = true;
    let ok = false;
    // Envío propio recién aceptado: el turno puede no haber arrancado todavía → un reintento.
    for (let i = 0; i < (abortRef.current === ctl ? 2 : 1) && !ok; i++) {
      if (i > 0) await new Promise((r) => setTimeout(r, 500));
      try {
        const res = await api.post<unknown>(`/assistant/sessions/${sid}/stop`, {});
        ok = !!res?.success;
      } catch {
        ok = false;
      }
    }
    if (!ok) {
      ctl.abort();
      return;
    }
    // Si el aviso de fin no llega a tiempo, se suelta la conexión (el turno ya se pidió cortar).
    // Si llegó antes, cortar una conexión ya terminada no hace nada.
    setTimeout(() => ctl.abort(), STOP_GRACE_MS);
  }, []);

  // Al montar: volver a la última conversación abierta en esta pestaña del navegador (si su
  // turno sigue corriendo en el servidor, se ve en vivo). Al salir: SOLO se corta la conexión;
  // el turno sigue en el servidor y queda guardado.
  useEffect(() => {
    mountedRef.current = true;
    const last = rememberedSession();
    if (last !== null) void openSessionInner(last, true);
    return () => {
      mountedRef.current = false;
      abortRef.current?.abort();
      attachRef.current?.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    sessions,
    sessionsLoading,
    current,
    messages,
    historyLoading,
    streaming,
    sending,
    liveText,
    liveTools,
    lastUsage,
    error,
    creating,
    setError,
    loadSessions,
    createSession,
    openSession,
    closeSession,
    deleteSession,
    send,
    stop,
    pendingActions,
    recentActions,
    actionBusy,
    resolvingBusy,
    actionOutcome,
    setActionOutcome,
    loadActions,
    confirmAction,
    cancelAction,
    loadClaimOptions,
    resolveClaim,
    alerts,
    alertBusy,
    followAlert,
    dismissAlert,
    // Vista única de avisos y lista larga (2026-10-21)
    sessionsLimit,
    loadMoreSessions,
    pendingAlerts,
    pendingAlertsLoading,
    loadPendingAlerts,
    followAlertIn,
    dismissAlertIn,
  };
}
